import Link from 'next/link';

/* -------------------------------------------------------------------------- */
/*  Section 0: Why Texting Works                                             */
/* -------------------------------------------------------------------------- */

function WhyTextingWorksSection() {
  return (
    <section id="why-texting" className="mb-12">
      <h2 className="text-2xl font-semibold mb-4 text-text-primary">
        Why Texting Works for Insurance Agencies
      </h2>
      <p className="text-text-secondary mb-4">
        Your clients&apos; communication preferences have shifted dramatically. Text messaging is no
        longer just a trend — it&apos;s increasingly an expectation for consumers interacting with
        businesses. For insurance agencies, texting offers unique advantages over traditional
        communication channels.
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">
        Texting vs. Email: By the Numbers
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
          <div className="text-3xl font-bold text-brand mb-2">98%</div>
          <p className="text-sm text-text-secondary mb-1">Text message open rate</p>
          <p className="text-xs text-text-secondary m-0">vs. ~20% for email</p>
        </div>
        <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
          <div className="text-3xl font-bold text-brand mb-2">90 sec</div>
          <p className="text-sm text-text-secondary mb-1">Average text response time</p>
          <p className="text-xs text-text-secondary m-0">vs. 90 minutes for email</p>
        </div>
        <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
          <div className="text-3xl font-bold text-brand mb-2">45%</div>
          <p className="text-sm text-text-secondary mb-1">Text message response rate</p>
          <p className="text-xs text-text-secondary m-0">vs. ~6% for email</p>
        </div>
        <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
          <div className="text-3xl font-bold text-brand mb-2">97%</div>
          <p className="text-sm text-text-secondary mb-1">Of Americans own a cellphone</p>
          <p className="text-xs text-text-secondary m-0">Nearly universal reach</p>
        </div>
      </div>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">
        Key Advantages for Agencies
      </h3>
      <div className="bg-card-bg border border-border rounded-2xl px-5 py-4 mb-6">
        <ul className="space-y-3 text-text-secondary">
          <li>
            <strong>Immediacy:</strong> Time-sensitive information (payment reminders, claim
            updates, appointment confirmations) reaches clients when it matters
          </li>
          <li>
            <strong>Engagement:</strong> Clients who prefer texting will actually respond, unlike
            emails that sit unread
          </li>
          <li>
            <strong>Relationship building:</strong> Texting feels personal and direct — it&apos;s
            how your clients communicate with friends and family
          </li>
          <li>
            <strong>Competitive advantage:</strong> Many agencies still rely solely on phone and
            email, making texting a differentiator
          </li>
          <li>
            <strong>Reduced phone tag:</strong> Quick questions get quick answers without
            scheduling calls
          </li>
          <li>
            <strong>Permission-based:</strong> Unlike cold calls, clients opt in to receive texts,
            ensuring they want to hear from you
          </li>
        </ul>
      </div>

      <div className="bg-brand-yellow/15 border border-brand-yellow/40 rounded-2xl px-5 py-3 mb-6">
        <p className="text-sm text-text-primary m-0">
          <strong>The Bottom Line:</strong> If your renewal reminder email goes unopened and your
          client&apos;s policy lapses, that&apos;s a relationship failure — and potential E&O
          exposure. A text message gets seen.
        </p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 1: CTIA Guidelines                                               */
/* -------------------------------------------------------------------------- */

function CtiaSection() {
  return (
    <>
      <section id="ctia" className="mb-12">
        <h2 className="text-2xl font-semibold mb-4 text-text-primary">Understanding CTIA Guidelines</h2>
        <p className="text-text-secondary mb-4">
          Beyond federal law (TCPA), the{' '}
          <strong>Cellular Telecommunications Industry Association (CTIA)</strong> establishes
          guidelines that carriers follow. While violating CTIA guidelines isn&apos;t illegal, it
          can result in your messages being filtered or blocked.
        </p>

        <div className="bg-card-bg border border-border rounded-2xl px-5 py-4 mb-6">
          <h4 className="font-semibold text-text-primary mb-3">
            TCPA vs. CTIA: What&apos;s the Difference?
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 pr-4 font-semibold text-text-primary"></th>
                  <th className="text-left py-2 px-4 font-semibold text-text-primary">TCPA</th>
                  <th className="text-left py-2 pl-4 font-semibold text-text-primary">CTIA</th>
                </tr>
              </thead>
              <tbody className="text-text-secondary">
                <tr className="border-b border-border">
                  <td className="py-2 pr-4 font-medium">What is it?</td>
                  <td className="py-2 px-4">Federal law</td>
                  <td className="py-2 pl-4">Industry guidelines</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-2 pr-4 font-medium">Enforced by</td>
                  <td className="py-2 px-4">FCC, FTC, private lawsuits</td>
                  <td className="py-2 pl-4">Carriers (AT&T, Verizon, T-Mobile)</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-2 pr-4 font-medium">Violation consequences</td>
                  <td className="py-2 px-4">$500-$1,500 per message</td>
                  <td className="py-2 pl-4">Messages filtered/blocked</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">Focus</td>
                  <td className="py-2 px-4">Consumer consent & privacy</td>
                  <td className="py-2 pl-4">Message quality & spam prevention</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">Key CTIA Requirements</h3>
        <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
          <li>
            <strong>Clear sender identification:</strong> Recipients must know who&apos;s messaging
            them
          </li>
          <li>
            <strong>Honest content:</strong> No deceptive messaging or hidden purposes
          </li>
          <li>
            <strong>Opt-out honoring:</strong> Process STOP requests immediately
          </li>
          <li>
            <strong>No link masking:</strong> Don&apos;t use public URL shorteners that hide the
            destination
          </li>
          <li>
            <strong>Appropriate content:</strong> No prohibited content categories (SHAFT: Sex,
            Hate, Alcohol, Firearms, Tobacco)
          </li>
          <li>
            <strong>Registered sender:</strong> Use properly registered phone numbers (A2P 10DLC)
          </li>
        </ul>

        <div className="bg-red-50 border border-red-200 rounded-2xl px-5 py-3 mb-6">
          <h4 className="font-semibold text-red-900 mb-2">URL Shortener Warning</h4>
          <p className="text-sm text-red-800 m-0">
            <strong>Do not use public URL shorteners</strong> like Bitly, TinyURL, or Google&apos;s
            shortener in your text messages. Carriers consider this &quot;link masking&quot; and may
            filter your messages. If you need to shorten a link, use a branded shortener or include
            the full URL.
          </p>
        </div>

        <div className="bg-brand-green/15 border border-brand-green/40 rounded-2xl px-5 py-3 mb-6">
          <h4 className="font-semibold text-text-primary mb-2">
            What Voley handles for CTIA compliance:
          </h4>
          <ul className="text-sm text-text-primary space-y-1 m-0">
            <li>
              • <strong>Opt-out processing:</strong> Automatically detects STOP and similar
              keywords, blocks further messages
            </li>
            <li>
              • <strong>Registered sender:</strong> Your number is properly registered through A2P
              10DLC
            </li>
            <li>
              • <strong>Message archiving:</strong> Complete records for any carrier audit
              requirements
            </li>
          </ul>
        </div>
      </section>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 2: Timing & Frequency + Message Content Best Practices            */
/* -------------------------------------------------------------------------- */

function TimingAndContentSection() {
  return (
    <>
      <section id="timing" className="mb-12">
        <h2 className="text-2xl font-semibold mb-4 text-text-primary">Timing & Frequency</h2>

        <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">When to Send</h3>
        <div className="bg-card-bg border border-border rounded-2xl px-5 py-4 mb-6">
          <div className="flex items-center justify-center mb-4">
            <div className="flex items-center space-x-2 text-text-secondary">
              <span className="text-2xl font-bold text-brand">9:00 AM</span>
              <span className="text-gray-400">—</span>
              <span className="text-2xl font-bold text-brand">9:00 PM</span>
            </div>
          </div>
          <p className="text-sm text-text-secondary text-center m-0">
            Only send messages between 9 AM and 9 PM{' '}
            <strong>in the recipient&apos;s local time zone</strong>. Messages outside these hours
            feel intrusive and may violate regulations.
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">Optimal Send Times</h3>
        <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
          <li>
            <strong>Appointment reminders:</strong> 24 hours before, then 1-2 hours before
          </li>
          <li>
            <strong>Payment reminders:</strong> 7 days before due date, then 1-2 days before
          </li>
          <li>
            <strong>Renewal notices:</strong> 30 days out, then 14 days, then 7 days
          </li>
          <li>
            <strong>General communications:</strong> Mid-morning (10-11 AM) or early afternoon (1-3
            PM) tend to have best response rates
          </li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">Frequency Guidelines</h3>
        <p className="text-text-secondary mb-4">
          Texting is valuable because it&apos;s not overused. Over-messaging will cause clients to
          opt out.
        </p>
        <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
          <li>
            <strong>Transactional messages:</strong> Send as needed for legitimate business purposes
          </li>
          <li>
            <strong>Marketing messages:</strong> No more than 2-4 per month maximum
          </li>
          <li>
            <strong>Be transparent:</strong> Tell clients what frequency to expect when they opt in
          </li>
        </ul>

        <div className="bg-brand/10 border border-brand/20 rounded-2xl px-5 py-3 mb-6">
          <p className="text-sm text-text-primary m-0">
            <strong>Time Zone Awareness:</strong> If your agency serves clients across multiple time
            zones, be mindful of when you&apos;re sending. A 9 AM message from your Utah office
            arrives at 8 AM for California clients — too early.
          </p>
        </div>
      </section>

      <section id="content" className="mb-12">
        <h2 className="text-2xl font-semibold mb-4 text-text-primary">
          Message Content Best Practices
        </h2>

        <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">
          1. Always Identify Yourself
        </h3>
        <p className="text-text-secondary mb-4">
          Your agency name should appear in every message. The sending number may be unfamiliar to
          clients.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-red-700 mb-2">DON&apos;T</p>
            <p className="text-sm text-text-primary font-mono">
              &quot;Your payment is due tomorrow. Please call to discuss.&quot;
            </p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-green-700 mb-2">DO</p>
            <p className="text-sm text-text-primary font-mono">
              &quot;Hi John, this is ABC Insurance. Your auto payment is due tomorrow. Questions?
              Reply here or call 801-555-1234.&quot;
            </p>
          </div>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">2. Be Clear and Specific</h3>
        <p className="text-text-secondary mb-4">
          Every message should have a clear purpose and, when appropriate, a specific call to
          action.
        </p>
        <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
          <li>Get to the point quickly — texts should be concise</li>
          <li>Include relevant details (dates, amounts, policy numbers when helpful)</li>
          <li>Make it easy to respond or take action</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">
          3. Use Professional but Conversational Tone
        </h3>
        <p className="text-text-secondary mb-4">
          Texting is inherently casual, but you&apos;re still representing your agency
          professionally.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-red-700 mb-2">TOO FORMAL</p>
            <p className="text-sm text-text-primary font-mono">
              &quot;Dear Valued Policyholder, Please be advised that your insurance premium
              payment...&quot;
            </p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-green-700 mb-2">JUST RIGHT</p>
            <p className="text-sm text-text-primary font-mono">
              &quot;Hi Sarah, quick reminder from Prior Insurance — your home policy renews on 3/15.
              Want to review your coverage? Just reply here.&quot;
            </p>
          </div>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">
          4. Include Opt-Out Instructions
        </h3>
        <p className="text-text-secondary mb-4">
          For marketing messages especially, include how to opt out:
        </p>
        <div className="bg-hover rounded-xl p-4 mb-4 font-mono text-sm text-text-primary">
          &quot;Reply STOP to opt out of promotional messages.&quot;
        </div>
        <div className="bg-brand-green/15 border border-brand-green/40 rounded-2xl px-5 py-3 mb-6">
          <p className="text-sm text-text-primary m-0">
            <strong>Voley handles this automatically:</strong> When a client replies STOP,
            UNSUBSCRIBE, CANCEL, END, or QUIT, Voley automatically blocks further messages to that
            contact and marks them as opted out. You&apos;ll see the opt-out status in their contact
            record.
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">
          5. Avoid These Common Mistakes
        </h3>
        <div className="bg-card-bg border border-border rounded-2xl px-5 py-4 mb-6">
          <ul className="space-y-3 text-text-secondary">
            <li>
              <strong>ALL CAPS:</strong> Feels like shouting and can trigger spam filters
            </li>
            <li>
              <strong>Excessive punctuation:</strong> &quot;ACT NOW!!!&quot; looks spammy
            </li>
            <li>
              <strong>Public URL shorteners:</strong> Use full URLs or branded shorteners only
            </li>
            <li>
              <strong>Slang or abbreviations:</strong> Keep it professional (&quot;ur&quot; instead
              of &quot;your&quot;)
            </li>
            <li>
              <strong>Emojis overuse:</strong> One or two is fine; a wall of emojis is not
            </li>
            <li>
              <strong>Vague messages:</strong> &quot;Call us about your policy&quot; — which policy?
              Why?
            </li>
          </ul>
        </div>
      </section>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 3: Insurance-Specific + Common Use Cases                          */
/* -------------------------------------------------------------------------- */

function InsuranceAndUseCasesSection() {
  return (
    <>
      <section id="insurance-specific" className="mb-12">
        <h2 className="text-2xl font-semibold mb-4 text-text-primary">
          Insurance-Specific Considerations
        </h2>

        <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">
          Coverage Cannot Be Bound or Altered via Text
        </h3>
        <div className="bg-red-50 border border-red-200 rounded-2xl px-5 py-3 mb-6">
          <p className="text-sm text-red-800 mb-3">
            <strong>Critical:</strong> Make it clear to clients and staff that coverage decisions
            cannot be made via text message. A client texting &quot;Yes, add that coverage&quot; is
            not the same as properly documented, signed authorization.
          </p>
          <p className="text-sm text-red-800 m-0">
            Consider adding this disclaimer to your consent form:{' '}
            <em>
              &quot;Coverage cannot be bound, altered, or cancelled via text message. Policy changes
              require proper documentation.&quot;
            </em>
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">What NOT to Send via Text</h3>
        <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
          <li>Full Social Security numbers</li>
          <li>Complete credit card or bank account numbers</li>
          <li>Detailed medical or health information (PHI)</li>
          <li>Driver&apos;s license numbers</li>
          <li>Login credentials or passwords</li>
          <li>Binding coverage confirmations</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">
          Annual Consent Re-verification
        </h3>
        <p className="text-text-secondary mb-4">
          Phone numbers change. Consider re-verifying consent annually — this ensures you still have
          the correct number and the client still wants to receive messages. Options include:
        </p>
        <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
          <li>Include consent re-confirmation on renewal paperwork</li>
          <li>Send an annual &quot;confirm your preferences&quot; message</li>
          <li>Verify phone numbers during any policy review</li>
        </ul>

        <div className="bg-brand-yellow/15 border border-brand-yellow/40 rounded-2xl px-5 py-3 mb-6">
          <p className="text-sm text-text-primary m-0">
            <strong>Why this matters:</strong> If a client&apos;s old number is recycled and
            assigned to someone else, you no longer have consent to message that number. Sending
            messages to the new owner is a TCPA violation.
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">
          State Insurance Regulations
        </h3>
        <p className="text-text-secondary mb-4">
          Some states have specific rules about insurance solicitation and marketing that may affect
          your texting practices. Check with your state insurance department and legal counsel,
          especially regarding:
        </p>
        <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
          <li>Marketing to prospects vs. existing clients</li>
          <li>Required disclosures in insurance communications</li>
          <li>Record retention requirements</li>
        </ul>
      </section>

      <section id="use-cases" className="mb-12">
        <h2 className="text-2xl font-semibold mb-4 text-text-primary">
          Common Use Cases & Sample Messages
        </h2>
        <p className="text-text-secondary mb-4">
          Here are effective text messages for common insurance agency scenarios. Customize these
          for your agency&apos;s voice and needs.
        </p>

        <div className="space-y-4 mb-6">
          <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
            <h4 className="font-semibold text-text-primary mb-2">Appointment Reminder</h4>
            <p className="text-sm text-text-secondary mb-2">
              Consent level: Transactional (prior express consent)
            </p>
            <div className="bg-hover rounded-lg p-3 font-mono text-sm text-text-primary">
              &quot;Hi [Name], this is [Agent] from [Agency]. Just a reminder about your policy
              review appointment tomorrow at 2:00 PM. Reply YES to confirm or call us at [phone] to
              reschedule.&quot;
            </div>
          </div>

          <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
            <h4 className="font-semibold text-text-primary mb-2">Payment Reminder</h4>
            <p className="text-sm text-text-secondary mb-2">
              Consent level: Transactional (prior express consent)
            </p>
            <div className="bg-hover rounded-lg p-3 font-mono text-sm text-text-primary">
              &quot;Hi [Name], [Agency] here. Your [auto/home] insurance payment of $[amount] is due
              on [date]. Questions about your bill? Reply here or call [phone].&quot;
            </div>
          </div>

          <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
            <h4 className="font-semibold text-text-primary mb-2">Renewal Notice</h4>
            <p className="text-sm text-text-secondary mb-2">
              Consent level: Transactional (prior express consent)
            </p>
            <div className="bg-hover rounded-lg p-3 font-mono text-sm text-text-primary">
              &quot;Hi [Name], this is [Agency]. Your [policy type] policy renews on [date]. Your
              renewal documents are ready — want to schedule a quick review? Reply here or call
              [phone].&quot;
            </div>
          </div>

          <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
            <h4 className="font-semibold text-text-primary mb-2">Claims Status Update</h4>
            <p className="text-sm text-text-secondary mb-2">
              Consent level: Transactional (prior express consent)
            </p>
            <div className="bg-hover rounded-lg p-3 font-mono text-sm text-text-primary">
              &quot;Hi [Name], [Agency] with an update on your claim. [Brief status — e.g.,
              &apos;The adjuster has completed their review.&apos;] Call [phone] or reply here with
              questions.&quot;
            </div>
          </div>

          <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
            <h4 className="font-semibold text-text-primary mb-2">Policy Document Ready</h4>
            <p className="text-sm text-text-secondary mb-2">
              Consent level: Transactional (prior express consent)
            </p>
            <div className="bg-hover rounded-lg p-3 font-mono text-sm text-text-primary">
              &quot;Hi [Name], your new insurance ID cards are ready! You can access them in your
              client portal or reply here and we&apos;ll email them to you. - [Agency]&quot;
            </div>
          </div>

          <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
            <h4 className="font-semibold text-text-primary mb-2">Welcome / Onboarding</h4>
            <p className="text-sm text-text-secondary mb-2">
              Consent level: Transactional (prior express consent)
            </p>
            <div className="bg-hover rounded-lg p-3 font-mono text-sm text-text-primary">
              &quot;Welcome to [Agency], [Name]! We&apos;re excited to have you. Your policy is now
              active. Save this number — text us anytime with questions. - [Agent Name]&quot;
            </div>
          </div>

          <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
            <h4 className="font-semibold text-text-primary mb-2">
              Cross-Sell / Policy Review Invitation
            </h4>
            <p className="text-sm text-text-secondary mb-2">
              Consent level: Marketing (prior express WRITTEN consent required)
            </p>
            <div className="bg-hover rounded-lg p-3 font-mono text-sm text-text-primary">
              &quot;Hi [Name], [Agency] here. Did you know bundling your auto and home could save
              you up to 15%? Reply if you&apos;d like a free quote. Reply STOP to opt out.&quot;
            </div>
          </div>
        </div>

        <div className="bg-brand/10 border border-brand/20 rounded-2xl px-5 py-3 mb-6">
          <p className="text-sm text-text-primary m-0">
            <strong>Tip:</strong> Create message templates in Voley for your most common scenarios.
            This ensures consistency, saves time, and helps maintain compliance across your team.
          </p>
        </div>
      </section>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 4: Documentation, Staff Training, Voley Features, Checklist       */
/* -------------------------------------------------------------------------- */

function DocumentationTrainingAndFeaturesSection() {
  return (
    <>
      <section id="documentation" className="mb-12">
        <h2 className="text-2xl font-semibold mb-4 text-text-primary">
          Documentation & E&O Protection
        </h2>
        <p className="text-text-secondary mb-4">
          Every text conversation with a client should be documented and retained, just like phone
          calls and emails. This isn&apos;t just good practice — it&apos;s essential for E&O
          protection.
        </p>

        <div className="bg-brand-yellow/15 border border-brand-yellow/40 rounded-2xl px-5 py-3 mb-6">
          <p className="text-sm text-text-primary m-0">
            <strong>Legal reality:</strong> A text message is as legally significant as any other
            form of communication. What is said in texts can be used in errors & omissions defense
            or other legal actions. Proper documentation is your protection.
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">What to Document</h3>
        <ul className="list-disc pl-6 text-text-secondary mb-4 space-y-2">
          <li>Complete message content (both sent and received)</li>
          <li>Timestamps for all messages</li>
          <li>Phone numbers involved</li>
          <li>Which team member sent the message</li>
          <li>Consent records (when and how consent was obtained)</li>
          <li>Opt-out requests and when they were processed</li>
        </ul>

        <div className="bg-brand-green/15 border border-brand-green/40 rounded-2xl px-5 py-3 mb-6">
          <p className="text-sm text-text-primary m-0">
            <strong>Voley handles this automatically:</strong> Every message sent and received
            through Voley is automatically archived with timestamps, sender information, and full
            conversation history. No manual documentation needed — it&apos;s all captured and
            searchable.
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">Why Documentation Matters</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
            <h4 className="font-semibold text-text-primary mb-2">E&O Defense</h4>
            <p className="text-sm text-text-secondary m-0">
              If a client claims they weren&apos;t notified about a renewal or payment, your text
              records prove otherwise.
            </p>
          </div>
          <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
            <h4 className="font-semibold text-text-primary mb-2">TCPA Defense</h4>
            <p className="text-sm text-text-secondary m-0">
              If accused of sending unauthorized messages, your consent records demonstrate
              compliance.
            </p>
          </div>
          <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
            <h4 className="font-semibold text-text-primary mb-2">Continuity</h4>
            <p className="text-sm text-text-secondary m-0">
              If a team member is out, others can see the full conversation history and continue
              serving the client.
            </p>
          </div>
          <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
            <h4 className="font-semibold text-text-primary mb-2">Compliance Audits</h4>
            <p className="text-sm text-text-secondary m-0">
              Complete records support regulatory compliance and carrier audit requirements.
            </p>
          </div>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">
          Use a Dedicated Business Number
        </h3>
        <p className="text-text-secondary mb-4">
          Staff texting clients from personal phones creates documentation gaps and E&O risk. Use a
          dedicated business number so all messages are captured in one place.
        </p>

        <div className="bg-brand-green/15 border border-brand-green/40 rounded-2xl px-5 py-3 mb-6">
          <p className="text-sm text-text-primary m-0">
            <strong>Voley provides this:</strong> Your agency gets a dedicated, registered business
            phone number. All team members send from this single number, ensuring every conversation
            is documented centrally and professionally.
          </p>
        </div>
      </section>

      <section id="staff-training" className="mb-12">
        <h2 className="text-2xl font-semibold mb-4 text-text-primary">
          Staff Training & Agency Policies
        </h2>
        <p className="text-text-secondary mb-4">
          Your compliance is only as good as your team&apos;s understanding and adherence. One team
          member&apos;s mistake exposes the entire agency.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">Training Topics</h3>
        <div className="bg-card-bg border border-border rounded-2xl px-5 py-4 mb-6">
          <ul className="space-y-3 text-text-secondary">
            <li>
              <strong>Consent requirements:</strong> When you can text, what consent is needed, how
              to document it
            </li>
            <li>
              <strong>Message content rules:</strong> What can and cannot be sent via text
            </li>
            <li>
              <strong>Opt-out handling:</strong> How to recognize and immediately honor opt-out
              requests
            </li>
            <li>
              <strong>Documentation:</strong> Your agency&apos;s process for recording all text
              conversations
            </li>
            <li>
              <strong>Timing restrictions:</strong> When messages can be sent (9 AM - 9 PM local
              time)
            </li>
            <li>
              <strong>Professional communication:</strong> Tone, content, and representing the
              agency appropriately
            </li>
            <li>
              <strong>What NOT to do:</strong> No binding coverage, no sensitive data, no personal
              phones
            </li>
          </ul>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">
          Create a Written SMS Policy
        </h3>
        <p className="text-text-secondary mb-4">
          Document your agency&apos;s texting policies and have all staff acknowledge them. Include:
        </p>
        <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
          <li>When texting is appropriate vs. when to use other channels</li>
          <li>Required message elements (identification, etc.)</li>
          <li>Prohibited content and actions</li>
          <li>Documentation requirements</li>
          <li>Consequences for policy violations</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">
          Regular Compliance Reviews
        </h3>
        <p className="text-text-secondary mb-4">Periodically audit your messaging practices:</p>
        <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
          <li>Review message content for compliance issues</li>
          <li>Verify opt-outs are being processed</li>
          <li>Check that consent records are complete</li>
          <li>Ensure all conversations are being documented</li>
          <li>Update training as regulations change</li>
        </ul>
      </section>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 5: Voley Features + Checklist + Resources + Need Help             */
/* -------------------------------------------------------------------------- */

function VoleyFeaturesAndChecklistSection({ external }: { external: boolean }) {
  return (
    <>
      <section id="voley-features" className="mb-12">
        <h2 className="text-2xl font-semibold mb-4 text-text-primary">
          How Voley Supports Best Practices
        </h2>
        <p className="text-text-secondary mb-4">
          Voley is designed with insurance agency workflows and compliance in mind. Here&apos;s how
          the platform supports the best practices in this guide:
        </p>

        <div className="grid grid-cols-1 gap-4 mb-6">
          <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
            <h4 className="font-semibold text-text-primary mb-2">Automatic Message Archiving</h4>
            <p className="text-sm text-text-secondary m-0">
              Every message sent and received is automatically saved with timestamps and user
              attribution. No manual documentation needed — it&apos;s all captured.
            </p>
          </div>

          <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
            <h4 className="font-semibold text-text-primary mb-2">Automatic Opt-Out Detection</h4>
            <p className="text-sm text-text-secondary m-0">
              Voley automatically recognizes opt-out keywords (STOP, UNSUBSCRIBE, etc.) and blocks
              further messages to that contact. No manual tracking required.
            </p>
          </div>

          <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
            <h4 className="font-semibold text-text-primary mb-2">Message Templates</h4>
            <p className="text-sm text-text-secondary m-0">
              Create pre-approved templates for common scenarios (renewals, payments, appointments).
              Ensures consistency and compliance across your team.
            </p>
          </div>

          <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
            <h4 className="font-semibold text-text-primary mb-2">Compliance Guardrails</h4>
            <p className="text-sm text-text-secondary m-0">
              Configure warnings that alert users when messages contain potentially problematic
              content before they send. Helps prevent compliance mistakes.
            </p>
          </div>

          <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
            <h4 className="font-semibold text-text-primary mb-2">Dedicated Business Number</h4>
            <p className="text-sm text-text-secondary m-0">
              All texting happens through your registered business number, not personal phones.
              Centralized, documented, professional.
            </p>
          </div>

          <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
            <h4 className="font-semibold text-text-primary mb-2">Team Visibility</h4>
            <p className="text-sm text-text-secondary m-0">
              All conversations are visible to authorized team members. No lost context when someone
              is out, and managers can review communications.
            </p>
          </div>

          <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
            <h4 className="font-semibold text-text-primary mb-2">A2P 10DLC Registration</h4>
            <p className="text-sm text-text-secondary m-0">
              Voley handles carrier registration for you, ensuring your messages are sent from a
              properly registered number with good deliverability.
            </p>
          </div>

          <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
            <h4 className="font-semibold text-text-primary mb-2">HawkSoft Integration</h4>
            <p className="text-sm text-text-secondary m-0">
              Conversations sync with your management system, maintaining complete client records in
              one place. See our{' '}
              <Link href="/docs/hawksoft" className="text-brand hover:text-brand/80">
                HawkSoft Integration Guide
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-4 text-text-primary">Quick Reference Checklist</h2>
        <p className="text-text-secondary mb-4">
          Use this checklist to ensure your agency&apos;s texting practices follow best practices:
        </p>

        <div className="bg-card-bg border border-border rounded-2xl px-5 py-4 mb-6">
          <div className="space-y-3">
            <label className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
              <span className="text-sm text-text-secondary">
                We have consent before texting any client
              </span>
            </label>
            <label className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
              <span className="text-sm text-text-secondary">
                Marketing messages have prior express WRITTEN consent
              </span>
            </label>
            <label className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
              <span className="text-sm text-text-secondary">
                Every message identifies our agency by name
              </span>
            </label>
            <label className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
              <span className="text-sm text-text-secondary">
                We only send between 9 AM and 9 PM local time
              </span>
            </label>
            <label className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
              <span className="text-sm text-text-secondary">
                Opt-out requests are honored immediately
              </span>
            </label>
            <label className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
              <span className="text-sm text-text-secondary">We don&apos;t use public URL shorteners</span>
            </label>
            <label className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
              <span className="text-sm text-text-secondary">
                Sensitive information (SSN, PHI) is never sent via text
              </span>
            </label>
            <label className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
              <span className="text-sm text-text-secondary">
                All conversations are documented and archived
              </span>
            </label>
            <label className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
              <span className="text-sm text-text-secondary">
                Staff use a dedicated business number, not personal phones
              </span>
            </label>
            <label className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
              <span className="text-sm text-text-secondary">
                Team has been trained on texting policies
              </span>
            </label>
            <label className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
              <span className="text-sm text-text-secondary">
                Coverage binding/alterations are NOT done via text
              </span>
            </label>
            <label className="flex items-start">
              <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
              <span className="text-sm text-text-secondary">
                We have a written SMS policy for the agency
              </span>
            </label>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-4 text-text-primary">Additional Resources</h2>
        <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
          <li>
            <Link href="/docs/tcpa" className="text-brand hover:text-brand/80">
              TCPA & SMS Compliance Guide
            </Link>{' '}
            — Federal law details, consent requirements, penalties
          </li>
          <li>
            <Link href="/docs/compliance" className="text-brand hover:text-brand/80">
              A2P 10DLC Compliance Guide
            </Link>{' '}
            — Carrier registration requirements
          </li>
          <li>
            <Link href="/docs/messaging" className="text-brand hover:text-brand/80">
              Sending Your First Message
            </Link>{' '}
            — Getting started with Voley messaging
          </li>
          <li>
            <Link href="/docs/hawksoft" className="text-brand hover:text-brand/80">
              HawkSoft Integration Guide
            </Link>{' '}
            — Connecting your management system
          </li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-4 text-text-primary">Need Help?</h2>
        <p className="text-text-secondary mb-4">
          Have questions about implementing these best practices with Voley? We&apos;re here to help
          you set up effective, compliant texting for your agency.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link
            href={external ? '/contact' : '/help?tab=support'}
            className="inline-flex items-center justify-center px-6 py-3 border-0 text-base font-medium rounded-full text-white bg-primary hover:shadow-lg transition-all hover:scale-[1.01] no-underline"
          >
            Contact Support
          </Link>
          {external && (
            <Link
              href="/request-demo"
              className="inline-flex items-center justify-center px-6 py-3 border border-border text-base font-medium rounded-full text-text-primary bg-card-bg hover:bg-hover hover:border-gray-300 transition-all no-underline"
            >
              Schedule a Demo
            </Link>
          )}
        </div>
      </section>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main Composed Component                                                   */
/* -------------------------------------------------------------------------- */

interface SmsBestPracticesContentProps {
  className?: string;
  /** 'external' points CTA links at marketing routes instead of in-app help routes */
  variant?: 'app' | 'external';
}

export default function SmsBestPracticesContent({ className, variant = 'app' }: SmsBestPracticesContentProps) {
  return (
    <div className={className}>
      <WhyTextingWorksSection />
      <CtiaSection />
      <TimingAndContentSection />
      <InsuranceAndUseCasesSection />
      <DocumentationTrainingAndFeaturesSection />
      <VoleyFeaturesAndChecklistSection external={variant === 'external'} />
    </div>
  );
}
